/**
 * Local-only app data: the `plume-local` IndexedDB database (listening
 * history) and small localStorage helpers shared by the scoped preference
 * stores (reading list, Home sections, weekly goal).
 *
 * Kept separate from `lib/db.ts` (`plume`: downloads, progress) so either
 * schema can evolve without coordinating version bumps.
 */

export const LOCAL_DB_NAME = 'plume-local';
export const LOCAL_DB_VERSION = 1;

export const HISTORY_STORE = 'historyEvents';
/** `[scope, bookId, timestampMs, id]`: per-book timeline pages, newest first with `prev` cursors. */
export const HISTORY_BY_BOOK_TIME = 'byBookTime';
/** Unique `[scope, bookId, dedupeKey]` (records without a dedupe key are not indexed). */
export const HISTORY_BY_DEDUPE = 'byDedupe';
/** `[scope, timestampMs]`: everything for one account (backup, clearing). */
export const HISTORY_BY_SCOPE = 'byScope';

export type LocalStoreName = typeof HISTORY_STORE;

let dbPromise: Promise<IDBDatabase> | null = null;

export function openLocalDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  const opening = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available.'));
      return;
    }
    const req = indexedDB.open(LOCAL_DB_NAME, LOCAL_DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(HISTORY_STORE)) {
        const os = db.createObjectStore(HISTORY_STORE, { keyPath: 'id' });
        os.createIndex(HISTORY_BY_BOOK_TIME, ['scope', 'bookId', 'timestampMs', 'id']);
        os.createIndex(HISTORY_BY_DEDUPE, ['scope', 'bookId', 'dedupeKey'], { unique: true });
        os.createIndex(HISTORY_BY_SCOPE, ['scope', 'timestampMs']);
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => {
        db.close();
        if (dbPromise === opening) dbPromise = null;
      };
      // The browser closed the connection (site data cleared, eviction, backing-store
      // failure): reopen on the next call instead of reusing a dead handle.
      db.onclose = () => {
        if (dbPromise === opening) dbPromise = null;
      };
      resolve(db);
    };
    req.onerror = () => {
      if (dbPromise === opening) dbPromise = null;
      reject(req.error ?? new Error('Could not open local storage.'));
    };
    req.onblocked = () => console.warn('[localDb] upgrade blocked by another tab');
  });
  dbPromise = opening;
  return opening;
}

/**
 * Starts a transaction. A handle the browser closed before `onclose` reached
 * us throws `InvalidStateError`; the connection is then reopened once.
 */
export async function localTransaction(store: LocalStoreName, mode: IDBTransactionMode): Promise<IDBTransaction> {
  const connection = openLocalDb();
  const db = await connection;
  try {
    return db.transaction(store, mode);
  } catch (err) {
    if ((err as DOMException | null)?.name !== 'InvalidStateError') throw err;
    if (dbPromise === connection) dbPromise = null;
    return (await openLocalDb()).transaction(store, mode);
  }
}

export function requestToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new DOMException('Transaction aborted', 'AbortError'));
  });
}

export function isConstraintError(err: unknown): boolean {
  return (err as DOMException | null)?.name === 'ConstraintError';
}

// ---------------------------------------------------------------- localStorage

/** `"<prefix>:<scope>"`, the Flutter key format (`reading_list_v1:{scope}`). */
export function scopedKey(prefix: string, scope: string): string {
  return `${prefix}:${scope.trim()}`;
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = safeStorage()?.getItem(key);
    return raw == null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

/** `null`/`undefined` removes the key. Quota and privacy-mode errors are swallowed. */
export function writeJson(key: string, value: unknown): void {
  try {
    const s = safeStorage();
    if (!s) return;
    if (value === undefined || value === null) s.removeItem(key);
    else s.setItem(key, JSON.stringify(value));
  } catch {
    /* quota / privacy mode */
  }
}

/** A JSON string array; anything else reads as empty. */
export function readStringList(key: string): string[] {
  const raw = readJson<unknown>(key, []);
  return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [];
}

/** Writes a string list, removing the key when it is empty (Flutter semantics). */
export function writeStringList(key: string, list: readonly string[]): void {
  writeJson(key, list.length ? list : null);
}

export function listStorageKeys(prefix: string): string[] {
  const s = safeStorage();
  if (!s) return [];
  const keys: string[] = [];
  try {
    for (let i = 0; i < s.length; i++) {
      const key = s.key(i);
      if (key?.startsWith(prefix)) keys.push(key);
    }
  } catch {
    /* storage unavailable */
  }
  return keys;
}

/**
 * Calls `onChange(key)` when another tab changes a matching localStorage key
 * (`key` is null when that tab cleared all storage).
 */
export function onStorageChange(matches: (key: string) => boolean, onChange: (key: string | null) => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handler = (e: StorageEvent) => {
    if (e.key === null || matches(e.key)) onChange(e.key);
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
